import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft, RefreshCw, CheckCircle } from 'lucide-react';
import { supabase } from '../../config/supabase';

const EmailConfirmationPending = ({ email, onBackToLogin }) => {
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [resendError, setResendError] = useState('');

  const handleResend = async () => {
    try {
      setResending(true);
      setResendError('');
      setResent(false);
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email
      });
      if (error) throw error;
      setResent(true);
    } catch (err) {
      setResendError(err.message || 'Failed to resend. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full max-w-md mx-auto"
    >
      <div className="card text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6"
        >
          <Mail className="w-10 h-10 text-blue-600" />
        </motion.div>

        <h2 className="text-2xl font-bold text-gray-900 mb-3">Check Your Email</h2>
        <p className="text-gray-600 mb-2">
          We've sent a confirmation link to
        </p>
        <p className="text-primary-600 font-semibold mb-6 break-all">{email}</p>
        <p className="text-sm text-gray-500 mb-8">
          Click the link in the email to verify your account. The link expires in 1 hour.
        </p>

        {resent && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 flex items-center justify-center space-x-2"
          >
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm">Confirmation email resent successfully!</span>
          </motion.div>
        )}

        {resendError && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm"
          >
            {resendError}
          </motion.div>
        )}

        <div className="space-y-3">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleResend}
            disabled={resending}
            className="w-full bg-gray-100 text-gray-700 font-semibold py-3 px-6 rounded-xl hover:bg-gray-200 transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50"
          >
            {resending ? (
              <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                <span>Resend Confirmation Email</span>
              </>
            )}
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onBackToLogin}
            className="w-full text-primary-600 font-medium py-3 px-6 rounded-xl hover:bg-primary-50 transition-all duration-200 flex items-center justify-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Sign In</span>
          </motion.button>
        </div>

        <p className="text-xs text-gray-400 mt-6">
          Didn't receive the email? Check your spam folder or try resending.
        </p>
      </div>
    </motion.div>
  );
};

export default EmailConfirmationPending;
